/**
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import dayjs from 'dayjs';
import dayjsCustomParseFormat from 'dayjs/plugin/customParseFormat';
import dayjsIsLeapYear from 'dayjs/plugin/isLeapYear';

import Numeric from './numeric/numeric';
import Stringify from './stringify';

// Vedlejší globální efekt sice není obecně chtěný jinde než v main, ale
// dokumentace Day.js říká, že jednou aktivovaný plugin při druhé a další
// registraci nic neudělá, a navíc tyto pluginy jsou bezparametrické.
dayjs.extend(dayjsCustomParseFormat);
dayjs.extend(dayjsIsLeapYear);

const dayFormat = 'YYYY-MM-DD';

// TODO: Některé testy jsou závislé na těchto konstantách a při změně můžou selhat.
export const minDayDateYear = 100;
export const maxDayDateYear = 9999;
export const minDayDate = /** @type {DayDate} */ (
  `${Numeric.toStringPadded(minDayDateYear, 4)}-01-01`
);
export const maxDayDate = /** @type {DayDate} */ (
  `${Numeric.toStringPadded(maxDayDateYear, 4)}-12-31`
);

/**
 * @private
 * @typedef {import('./day-of-week').DayOfWeek} DayOfWeek
 * @typedef {import('dayjs').Dayjs} Dayjs
 */

/**
 * @typedef {string} DayDate Datum v ISO 8601 YYYY-MM-DD formátu na úrovni dní
 * interpretované v lokální časové zóně.
 *
 * Prakticky je rozsah roků 0100-9999 (omezení použité knihovny Day.js).
 *
 * Obecně všechny veřejné funkce vyhazují TypeError pro chyby na vstupu
 * a RangeError na výstupu, pokud mohlo dojít k operaci mimo rozsah.
 */

// TODO: Pokusy o lepší typ než string, např. enum v omezeném rozsahu let
// 1900-2999, kde se počet dní vleze do maximálního limitu 100 tisíc položek TS.
// TODO: Definice typu validujícího obsah stringu:
// - https://stackoverflow.com/questions/51445767/how-to-define-a-regex-matched-string-type-in-typescript
// - Template literal types and mapped type 'as' clauses:
//   https://github.com/microsoft/TypeScript/pull/40336
// - Suggestion: Regex-validated string type:
//   https://github.com/microsoft/TypeScript/issues/6579
// - Regex-validated string types (feedback reset):
//   https://github.com/microsoft/TypeScript/issues/41160
// - https://stackoverflow.com/questions/8647893/regular-expression-leap-years-and-more
/*
 * @typedef {Extract<IsoYear, `${
 *   | 1904 | 1908 | 1912 | 1916 | 1920 | 1924 | 1928 | 1932 | 1936 | 1940
 *   | 1944 | 1948 | 1952 | 1956 | 1960 | 1964 | 1968 | 1972 | 1976 | 1980
 *   | 1984 | 1988 | 1992 | 1996 | 2000 | 2004 | 2008 | 2012 | 2016 | 2020
 *   | 2024 | 2028 | 2032 | 2036 | 2040 | 2044 | 2048 | 2052 | 2056 | 2060
 *   | 2064 | 2068 | 2072 | 2076 | 2080 | 2084 | 2088 | 2092 | 2096
 * }`>} LeapYear
 * @private
 * @typedef {0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9} Digit
 * @typedef {`${19 | 20}${Digit}${Digit}`} IsoYear
 * @typedef {`0${Exclude<Digit, 0>}` | `1${0 | 1 | 2}`} IsoMonth
 * @typedef {`0${Exclude<Digit, 0>}` | `${1 | 2}${Digit}` | `3${0 | 1}`} IsoDay
 * @typedef {`0${Exclude<Digit, 0>}` | `1${Digit}` | `2${Exclude<Digit, 9>}`} IsoDayAllMonths
 * @typedef {never
 *   | `${IsoYear}-${IsoMonth}-${`0${Exclude<Digit, 0>}` | `1${Digit}` | `2${Exclude<Digit, 9>}`}`
 *   | `${LeapYear}-02-29`
 *   | `${IsoYear}-${Exclude<IsoMonth, '02'>}-${'29' | '30'}`
 *   | `${IsoYear}-${'01' | '03' | '05' | '07' | '08' | '10' | '12'}-31`
 * } DayDateEnum
 */

/**
 * Utilitní knižnice pro spracovávaní datumu ve formátu ISO 8601 YYYY-MM-DD
 * TODO: Problém s float reprezentací počtu ms od UNIX epochy:
 * Sun Mar 01 0099 00:57:44 GMT+0057 (středoevropský standardní čas)
 * date: new Date('0099-02-29'),
 * Invalid value 0099-03-01 at #fromYMD.
 * result: RangeError
 * Asi by chtělo zjistit rozsah dayjs, kdy nedochází ke ztrátě přesnosti a pak
 * omezit validaci na tento rozsah.
 *
 * Podle zdrojáků se problém nepřesnosti časové zóny prý ošetřuje podobně jako
 * v moment.js:
 * - https://github.com/iamkun/dayjs/blob/dev/src/index.js#L344
 * - https://github.com/moment/moment/issues/1835
 * - https://github.com/moment/moment/pull/1871
 */

/**
 * K datu přičte počet dní.
 * @param {DayDate} date
 * @param {number} days Počet dní, které se přičtou k datu (celé číslo, může být záporné).
 * @returns {DayDate}
 * @throws {TypeError} Pokud je datum nebo počet dní na vstupu nevalidní.
 * @throws {RangeError} Pokud je datum na na výstupu mimo rozsah.
 */
export function addDays(date, days) {
  const errorArgs = /** @type {const} */ (['addDays', date, days]);
  validateInputInteger(days, ...errorArgs);
  return fromDayjs(toDayjs(date, ...errorArgs).add(days, 'day'), ...errorArgs);
}

/**
 * K datu přičte počet let.
 *
 * Pokud je na vstupu přestupný den 29. února, tak v případě výsledného
 * nepřestupného roku "zaokrouhlí" neexistující den na 28. února.
 * @param {DayDate} date
 * @param {number} years Počet let, které se přičtou k datu (celé číslo, může být záporné).
 * @returns {DayDate}
 * @throws {TypeError} Pokud je datum nebo počet let na vstupu nevalidní.
 * @throws {RangeError} Pokud je datum na na výstupu mimo rozsah.
 */
export function addYears(date, years) {
  const errorArgs = /** @type {const} */ (['addYears', date, years]);
  validateInputInteger(years, ...errorArgs);
  // Podle Hyrumova zákona závisíme na implementaci dayjs, jelikož volaná
  // funkce dělá stejné zaokrouhlení přestupného dne, jako je předepsáno
  // ve specifikaci této funkce, takže nakonec nemusíme explicitně ošetřovat.
  return fromDayjs(
    toDayjs(date, ...errorArgs).add(years, 'year'),
    ...errorArgs
  );
}

/**
 * Porovná dva datumy (ignoruje rok u prvního) podle měsíce a roku.
 * První datum je zadáno jako DayDate a druhé pomocí měsíce a dne.
 * @param {DayDate} date1 První datum s ignorovaným rokem.
 * @param {number} month2 Měsíc v roce druhého data [1-12].
 * @param {number} day2 Den v měsíci druhého data [1-31].
 * @returns {number} Hodnoty <0, 0, >0 dle `compareFunction` v `Array.sort`.
 * @throws {RangeError} Pokud je datum nevalidní.
 */
export function compareMonthDay(date1, month2, day2) {
  try {
    return getMonth(date1) - month2 || getDay(date1) - day2;
  } catch {
    // Potřebujeme přemapovat typ chyby a hlášku.
    throwInput('compareMonthDay', date1, month2, day2);
  }
}

/**
 * Vrátí den v týdnu.
 * @param {DayDate} date
 * @returns {DayOfWeek}
 * @throws {RangeError} Pokud je datum nevalidní.
 */
export function getDayOfWeek(date) {
  // Hodnoty pro dny v týdnu v dayjs (a JS Date) a naše DayOfWeek jsou stejné,
  // takže můžeme jen jednoduše přetypovat.
  return /** @type {DayOfWeek} */ (toDayjs(date, 'getDayOfWeek', date).day());
}

// TODO #536: Toto je bohužel flaky kód, protože záleží na lokální časové zóně
// a úplnosti/přesnosti TZ databázi různých OS.
//
// Na Windows totiž např. pro CET před 1891-09-30T23:02:16.000Z došlo ke změně
// TZ offsetu z GMT+0100 na GMT+0057 (přesněji 00:57:44)! Tím pádem pak při
// pohledu hodně do minulosti nemáme celé dny a dojde k nepřesnostem o jedničku.
/**
 * Vrátí rozdíl v dnech mezi dvěma daty.
 *
 * Když je date1 větší než date2, vrátí kladné číslo, jinak záporné, nebo 0
 * pro stejná data.
 * @param {DayDate} date1
 * @param {DayDate} date2
 * @returns {number}
 * @throws {TypeError} Pokud je nějaké datum nevalidní.
 */
export function diffDays(date1, date2) {
  const errorArgs = /** @type {const} */ (['diffDays', date1, date2]);
  return toDayjs(date1, ...errorArgs).diff(toDayjs(date2, ...errorArgs), 'day');
}

/**
 * Získá validní DayDate z řetězce nebo vyhodí chybu.
 * @param {DayDate | string} date
 * @returns {DayDate}
 * @throws {TypeError} Pokud je datum nevalidní.
 */
export function from(date) {
  toDayjs(date, 'from', date);
  return date;
}

// TODO: Zajímavost ohledně změny časové zóny (offsetu od UTC) pro CET v minulosti.
// - https://stackoverflow.com/questions/50609860/browsers-time-zones-chrome-67-error-historic-timezone-changes
// expect(
//   new Date('1891-09-30T23:02:15.999Z').getTimezoneOffset()
// ).to.equal(-57);
// expect(
//   new Date('1891-09-30T23:02:16.000Z').getTimezoneOffset()
// ).to.equal(-60);

/**
 * Vytvoří DayDate z Date objektu.
 * @param {Date} date
 * @returns {DayDate}
 * @throws {TypeError} Pokud je rozsah datumu nevalidní.
 */
export function fromDate(date) {
  try {
    return fromYMD(date.getFullYear(), date.getMonth() + 1, date.getDate());
  } catch {
    // Potřebujeme přemapovat typ chyby a hlášku.
    throwInput('fromDate', date);
  }
}

/**
 * Vytvoří DayDate z Dayjs objektu.
 *
 * Typicky se používá na výstup z funkce, proto vyhazuje RangeError a nikoliv
 * TypeError jako ostatní fromXxx funkce.
 * @param {Dayjs} date
 * @param {string} fnName
 * @param {...any} args
 * @returns {DayDate}
 * @throws {RangeError} Pokud je rozsah datumu nevalidní.
 */
function fromDayjs(date, fnName, ...args) {
  // Pozor, že při nevalidním date na vstupu format v klidu vrátí řetězec
  // 'Invalid Date' místo výjimky! Z toho důvodu je lepší mít ošetření centrálně,
  // ale výkonnostně na tom budou biti všichni. Možná by bylo lepší ošetřovat
  // jen na místech, které mohou přijmout nevalidní datum (např. fromYMD).
  const result = date.format(dayFormat);
  if (isValid(result)) {
    return result;
  }
  throwOutput(fnName, ...args);
}

/**
 * Vytvoří validní datum pro rok, měsíc a den.
 * @param {number} year Rok v rozsahu [100, 9999].
 * @param {number} month Měsíc v rozsahu [1, 12].
 * @param {number} day Den v měsíci v rozsahu [1, 31].
 * @returns {DayDate}
 * @throws {TypeError} Pokud je datum nevalidní.
 */
export function fromYMD(year, month, day) {
  const errorArgs = /** @type {const} */ (['fromYMD', year, month, day]);
  if (
    !Numeric.isIndex(year) ||
    year < minDayDateYear ||
    year > maxDayDateYear ||
    !Numeric.isIndex(month) ||
    month < 1 ||
    month > 12 ||
    !Numeric.isIndex(day) ||
    day < 1 ||
    day > 31
  ) {
    throwInput(...errorArgs);
  }
  const result = `${Numeric.toStringPadded(year, 4)}-${Numeric.toStringPadded(
    month,
    2
  )}-${Numeric.toStringPadded(day, 2)}`;
  try {
    return fromDayjs(toDayjs(result, ...errorArgs), ...errorArgs);
  } catch {
    // fromDayjs vyhazuje RangeError, ale my zde potřebujeme input TypeError.
    throwInput(...errorArgs);
  }
}

/**
 * Vráti validní datum pro rok, měsíc a den nebo undefined, pokud je nevalidní.
 * @param {number} year Rok v rozsahu [100, 9999].
 * @param {number} month Měsíc v rozsahu [1, 12].
 * @param {number} day Den v měsíci v rozsahu [1, 31].
 * @returns {DayDate | undefined}
 */
export function fromYMDOrUndefined(year, month, day) {
  try {
    return fromYMD(year, month, day);
  } catch {}
}

/**
 * Vrátí den v měsíci.
 * @param {DayDate} date
 * @returns {number}
 * @throws {TypeError} Pokud je datum nevalidní.
 */
export function getDay(date) {
  return toDayjs(date, 'getDay', date).date();
}

/**
 * Vrátí měsíc.
 * @param {DayDate} date
 * @returns {number} V rozsahu [1, 12].
 * @throws {TypeError} Pokud je datum nevalidní.
 */
export function getMonth(date) {
  // Dayjs.month() (stejně jako Date.getMonth()) má range [0, 11].
  return toDayjs(date, 'getMonth', date).month() + 1;
}

/**
 * Vrátí rok.
 * @param {DayDate} date
 * @returns {number}
 * @throws {TypeError} Pokud je datum nevalidní.
 */
export function getYear(date) {
  return toDayjs(date, 'getYear', date).year();
}

/**
 * Porovná datumy, zda jsou stejné.
 * @param {DayDate} date1
 * @param {DayDate} date2
 * @returns {boolean}
 * @throws {TypeError} Pokud je nějaké datum nevalidní.
 */
export function isEqual(date1, date2) {
  const errorArgs = /** @type {const} */ (['isEqual', date1, date2]);
  return toDayjs(date1, ...errorArgs).isSame(toDayjs(date2, ...errorArgs));
}

/**
 * Zkontroluje, zda je datum v přestupném roce přestupný den (29. února).
 * @param {DayDate} date
 * @returns {boolean}
 * @throws {TypeError} Pokud je datum nevalidní.
 */
export function isLeapDay(date) {
  const d = toDayjs(date, 'isLeapDay', date);
  return (
    d.isLeapYear() &&
    // Pozor, Day.js stejně jako JS Date indexuje měsíce od nuly,
    // proto porovnání na 1 pro únor.
    d.month() === 1 &&
    d.date() === 29
  );
}

/**
 * Zjistí, zda je rok datumu přestupný.
 * @param {DayDate} date
 * @returns {boolean}
 * @throws {TypeError} Pokud je datum nevalidní.
 */
export function isLeapYear(date) {
  return toDayjs(date, 'isLeapYear', date).isLeapYear();
}

/**
 * Porovná dvě data, zda je date1 menší než date2.
 * @param {DayDate} date1
 * @param {DayDate} date2
 * @returns {boolean}
 * @throws {TypeError} Pokud je nějaké datum nevalidní.
 */
export function isLess(date1, date2) {
  const errorArgs = /** @type {const} */ (['isLess', date1, date2]);
  return toDayjs(date1, ...errorArgs).isBefore(toDayjs(date2, ...errorArgs));
}

/**
 * Porovná dvě data, zda je date1 menší nebo rovné než date2.
 * @param {DayDate} date1
 * @param {DayDate} date2
 * @returns {boolean}
 * @throws {TypeError} Pokud je nějaké datum nevalidní.
 */
export function isLessOrEqual(date1, date2) {
  const errorArgs = /** @type {const} */ (['isLessOrEqual', date1, date2]);
  return !toDayjs(date1, ...errorArgs).isAfter(toDayjs(date2, ...errorArgs));
}

/**
 * Zkontroluje, zda je řetězec validní DayDate.
 * @param {DayDate | string} date
 * @returns {date is DayDate}
 */
export function isValid(date) {
  let valid = false;
  try {
    from(date);
    valid = true;
  } catch {}
  return valid;
}

/**
 * Převede DayDate na Date.
 * @param {DayDate} date
 * @returns {Date}
 * @throws {TypeError} Pokud je rozsah datumu nevalidní.
 */
export function toDate(date) {
  const d = toDayjs(date, 'toDate', date);
  return new Date(d.year(), d.month(), d.date());
}

/**
 * Převede string ve formátu ISO 8601 YYYY-MM-DD na objekt Dayjs.
 * @param {string} date
 * @param {string} fnName Název funkce.
 * @param {...any} args Argumenty funkce.
 * @returns {Dayjs}
 * @throws {TypeError} Pokud je datum nevalidní.
 */
function toDayjs(date, fnName, ...args) {
  // Posledný parameter 'true' znamená striktní parsování, t.j. pre 'false'
  // by síce pre nejaký nezmysel ako 'abc' vráti validace false (t.j. 'abc'
  // => Invalid Date alebo '2021-0x-29' => Invalid Date), ale pre nezmyselný
  // dátum síce ISO formáte napr. '2020-15-65' si to sám odvodí ako validný
  // dátum ako '2021-04-04' (t.j. 2020-15-65 => 2021-04-04) alebo '2021-02-29'
  // (t.j. '2021-02-29' => '2021-03-01'). 'true' zaručuje, že všetky takéto
  // podoby interpretuje na Invalid Date.
  // TODO: Otázka co je lepší, zda vyhodit rovnou chybu nebo nechat ošetření na volajícím?
  // Jenže možná tak jediný isValid by potřeboval, aby to nevrhlo a v ostatních případech
  // by bylo vhodné vrhnout, jinak by se to muselo ošetřovat explicitně absolutně všude.
  const d = dayjs(date, dayFormat, true);
  if (!d.isValid() || d.year() < minDayDateYear || d.year() > maxDayDateYear) {
    throwInput(fnName, ...args);
  }
  return d;
}

/**
 * @param {string} fnName
 * @param  {...any} args
 * @returns {never}
 */
function throwInput(fnName, ...args) {
  throw new TypeError(Stringify.call(fnName, ...args));
}

/**
 * @param {string} fnName
 * @param  {...any} args
 * @returns {never}
 */
function throwOutput(fnName, ...args) {
  throw new RangeError(Stringify.call(fnName, ...args));
}

/**
 * Vrátí aktuální den v lokální časové zóně.
 * @returns {DayDate}
 */
export function getToday() {
  return fromDayjs(dayjs(), 'getToday');
}

/**
 * @param {number} value
 * @param {string} fnName
 * @param  {...any} args
 * @throws {TypeError}
 */
function validateInputInteger(value, fnName, ...args) {
  if (!Number.isInteger(value)) {
    throwInput(fnName, ...args);
  }
}

export const internal = {
  dayFormat,
  fromDayjs,
  toDayjs
};
